import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { toConsentBlock, type ConsentBlockState } from '../utils/healthcareConsent';
import { useFollowSpec } from '../hooks/useFollowSpec';
import { useInterviewRecordOptions } from '../hooks/useInterviewRecordOptions';
import LatestInterviewRecord from '../components/LatestInterviewRecord';
import InterviewScheduleList from '../components/InterviewScheduleList';
import { formatRecordedAt } from '../utils/interviewRecords';
import {
  allowedFollowStatuses,
  followStatusLabel,
  followTransitionErrorFrom,
  isKnownFollowStatus,
} from '../utils/followStatus';
import {
  healthcareApi,
  type PersonDetailResponse,
} from '../api/client';

/** HLT-S02 個人コンディション詳細（API 連携） */
export default function HealthcarePersonDetail() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const { getToken } = useAuth();
  const [person, setPerson] = useState<PersonDetailResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [consentBlock, setConsentBlock] = useState<ConsentBlockState | null>(null);
  const [followStatus, setFollowStatus] = useState('');
  const [followNotes, setFollowNotes] = useState('');
  const [updatingFollow, setUpdatingFollow] = useState(false);
  const [followError, setFollowError] = useState<string | null>(null);
  const followSpec = useFollowSpec();
  const recordOptions = useInterviewRecordOptions();

  useEffect(() => {
    const token = getToken();
    if (!token || !id) {
      setLoading(false);
      return;
    }
    setError(null);
    setConsentBlock(null);
    setLoading(true);
    healthcareApi
      .getPerson(id, token)
      .then((data) => {
        setPerson(data);
        setFollowStatus(data.follow_status ?? '');
        // 今のメモを入れておく（状況だけ変えたときに、メモが空で上書きされて消えないように）
        setFollowNotes(data.follow_notes ?? '');
      })
      .catch((err) => {
        // BE が CONSENT_REQUIRED / ANONYMOUS_MODE_RESTRICTED を返した場合は専用 UI。
        // 判定は面談設定（HC-S03）と共通のユーティリティに集約している（課題 #26）
        const blocked = toConsentBlock(err);
        if (blocked) {
          setConsentBlock(blocked);
          return;
        }
        setError(err instanceof Error ? err.message : t('dashboard.errorLoad'));
      })
      .finally(() => setLoading(false));
  }, [id, getToken, t]);

  // フォロー状況は 4 値。今の状況から選べる値だけを出す（課題 #71 / BE PR #143）
  const currentFollow = person?.follow_status || null;
  const followChoices = allowedFollowStatuses(followSpec, currentFollow);
  const selectedFollow = followChoices.includes(followStatus) ? followStatus : followChoices[0] ?? '';

  const handleUpdateFollow = (e: React.FormEvent) => {
    e.preventDefault();
    const token = getToken();
    if (!token || !id || !selectedFollow) return;
    setFollowError(null);
    setUpdatingFollow(true);
    healthcareApi
      .updateFollowStatus(id, { status: selectedFollow, notes: followNotes.trim() || undefined }, token)
      .then((res) => {
        setPerson((prev) =>
          prev
            ? {
                ...prev,
                follow_status: res.status,
                // メモを返す BE のときだけ、画面のメモも送った内容にそろえる
                ...(prev.follow_notes !== undefined
                  ? { follow_notes: followNotes.trim() || null, follow_updated_at: new Date().toISOString() }
                  : {}),
              }
            : null
        );
        setFollowStatus(res.status);
      })
      .catch((err) => {
        // ほかの方が先に状況を変えていたなど。BE の今の状況に合わせて、選べる値を直す
        const transition = followTransitionErrorFrom(err);
        if (transition) {
          setPerson((prev) => (prev ? { ...prev, follow_status: transition.currentStatus } : null));
          setFollowError(
            t('healthcare.followTransitionNotAllowed', {
              current: followStatusLabel(followSpec, transition.currentStatus) || followStatusLabel(followSpec, 'not_started'),
            })
          );
          return;
        }
        // 同意が無い方の更新は BE が 403 CONSENT_REQUIRED で止める（#71 / BE PR #131）
        if (toConsentBlock(err)) {
          setFollowError(t('healthcare.consentRequiredHeader'));
          return;
        }
        setFollowError(err instanceof Error ? err.message : t('common.error'));
      })
      .finally(() => setUpdatingFollow(false));
  };

  const mcsRows = person?.mcs_timeseries ?? [];

  if (loading) {
    return (
      <div className="page healthcare-person-page">
        <div className="dashboard-loading">{t('common.loading')}</div>
      </div>
    );
  }

  if (consentBlock) {
    return (
      <div className="page healthcare-person-page">
        <h1>{t('healthcare.personDetail')}</h1>
        <p className="healthcare-anon-label">{t('healthcare.anonId')}: {id ?? '—'}</p>
        <div className="healthcare-card">
          {consentBlock.kind === 'anonymous_restricted' ? (
            <p className="healthcare-error-text">{t('healthcare.anonymousModeRestricted')}</p>
          ) : (
            <>
              <p className="healthcare-error-text">{t('healthcare.consentRequiredHeader')}</p>
              {consentBlock.missing.length > 0 && (
                <ul className="healthcare-missing-consents-list">
                  {consentBlock.missing.map((m, i) => (
                    <li key={`${m.purpose}-${m.data_type}-${i}`}>{m.label_ja}</li>
                  ))}
                </ul>
              )}
              <p className="healthcare-consent-explain">{t('healthcare.consentRequiredHint')}</p>
            </>
          )}
        </div>
        <p className="healthcare-back">
          <Link to="/healthcare">{t('common.back')} → {t('healthcare.backToCandidates')}</Link>
        </p>
      </div>
    );
  }

  if (error || !person) {
    return (
      <div className="page healthcare-person-page">
        <p className="healthcare-error-text">{error ?? t('dashboard.errorLoad')}</p>
        <p className="healthcare-back">
          <Link to="/healthcare">{t('common.back')} → {t('healthcare.backToCandidates')}</Link>
        </p>
      </div>
    );
  }

  return (
    <div className="page healthcare-person-page">
      <h1>{t('healthcare.personDetail')}</h1>
      <p className="healthcare-anon-label">{t('healthcare.anonId')}: {id ?? '—'}</p>

      <div className="healthcare-card">
        <h3>{t('healthcare.consent')}</h3>
        {person.consent_granted ? (
          <>
            <span className="healthcare-badge healthcare-badge-ok">{t('healthcare.consentAnonymous')}</span>
            <span className="healthcare-badge healthcare-badge-ok">{t('healthcare.consentPersonal')}</span>
          </>
        ) : (
          <span className="healthcare-badge healthcare-badge-none">{t('healthcare.consentNone')}</span>
        )}
        {!person.consent_granted && <p className="healthcare-consent-explain">{t('healthcare.consentNoneExplain')}</p>}
      </div>

      {mcsRows.length > 0 && (
        <div className="healthcare-card">
          <h3>{t('healthcare.mcsTrend')}</h3>
          <table className="healthcare-table">
            <thead>
              <tr>
                <th scope="col">{t('history.date')}</th>
                <th scope="col">{t('dashboard.mcsScore')}</th>
              </tr>
            </thead>
            <tbody>
              {mcsRows.slice(0, 14).map((row, i) => (
                <tr key={i}>
                  <td>{(row.date as string) ?? (row.report_date as string) ?? '—'}</td>
                  <td>{(row.score as number) ?? (row.mcs as number) ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/*
        「直近 7 日の日ごとのまとめ」（ハイライト）の欄は、中身ができるまで出さない（課題 #51 の 3。2026-10-20 高尾さん）。
        今の BE は日付と空のまとめを返すため「—: —」が並んでいた。中身（date と items）ができたら #51 の 6 で出し直す
      */}

      <div className="healthcare-card">
        <h3>{t('healthcare.followStatus')}</h3>
        <form onSubmit={handleUpdateFollow} className="healthcare-form">
          <p className="healthcare-follow-current" data-testid="follow-current">
            {currentFollow == null
              ? t('healthcare.followCurrentNone')
              : isKnownFollowStatus(followSpec, currentFollow)
              ? t('healthcare.followCurrent', { label: followStatusLabel(followSpec, currentFollow) })
              : t('healthcare.followCurrentLegacy', { value: currentFollow })}
          </p>
          {/* 今のメモ（BE がメモを返すようになってから出す。2026-09-30 高尾さん「見たいのに見られない」） */}
          {person.follow_notes !== undefined && (
            <div className="healthcare-follow-notes" data-testid="follow-notes">
              <p className="healthcare-follow-notes-label">
                {t('healthcare.followNotesCurrent')}
                {person.follow_updated_at && (
                  <span className="healthcare-follow-notes-updated">
                    {t('healthcare.followUpdatedAt', { at: formatRecordedAt(person.follow_updated_at) })}
                  </span>
                )}
              </p>
              <p className="healthcare-follow-notes-body">{person.follow_notes || t('healthcare.followNotesNone')}</p>
            </div>
          )}
          <div className="healthcare-form-group">
            <label htmlFor="follow-status-select">{t('healthcare.followStatusLabel')}</label>
            <select
              id="follow-status-select"
              value={selectedFollow}
              onChange={(e) => setFollowStatus(e.target.value)}
            >
              {followChoices.map((code) => (
                <option key={code} value={code}>
                  {code === currentFollow
                    ? t('healthcare.followKeep', { label: followStatusLabel(followSpec, code) })
                    : followStatusLabel(followSpec, code)}
                </option>
              ))}
            </select>
            <p className="healthcare-form-hint">{t('healthcare.followStatusHint')}</p>
          </div>
          <div className="healthcare-form-group">
            <label htmlFor="follow-notes">{t('healthcare.followNotes')}</label>
            <textarea
              id="follow-notes"
              rows={2}
              value={followNotes}
              onChange={(e) => setFollowNotes(e.target.value)}
              placeholder={t('healthcare.followNotesPlaceholder')}
              className="healthcare-textarea"
              maxLength={500}
            />
          </div>
          {followError && <p className="healthcare-form-error">{followError}</p>}
          <button type="submit" className="healthcare-btn-primary" disabled={updatingFollow}>
            {updatingFollow ? t('common.loading') : t('healthcare.followUpdate')}
          </button>
        </form>
        <Link to={`/healthcare/person/${id}/interview`} className="healthcare-btn-primary healthcare-btn-link">
          {t('healthcare.interviews')}
        </Link>
      </div>

      {/* 登録済みの面談予定（メモを含む）。予定を登録すると、この画面に戻ってくる */}
      {person.consent_granted && id && <InterviewScheduleList personId={id} limit={3} />}

      {/* 直近の面談記録（課題 #71）。同意の無い方は BE が 403 にするため出さない */}
      {recordOptions.status === 'ready' && person.consent_granted && id && (
        <LatestInterviewRecord personId={id} options={recordOptions.options} />
      )}

      <p className="healthcare-back">
        <Link to="/healthcare">{t('common.back')} → {t('healthcare.backToCandidates')}</Link>
      </p>
    </div>
  );
}
